<template>
  <div v-if="isOpen" class="qr-modal-backdrop" @click.self="close">
    <div class="qr-modal-card">
      <div class="qr-modal-header">
        <div class="d-flex align-items-center">
          <div class="qr-icon-bubble me-3">
            <i class="material-icons">qr_code_2</i>
          </div>
          <div>
            <h5 class="mb-0 text-white font-weight-bolder">QR Code da Campanha</h5>
            <p class="text-xs text-white opacity-8 mb-0">Para banners, cartazes, panfletos e stories</p>
          </div>
        </div>
        <button type="button" class="btn-close-modal" @click="close">
          <i class="material-icons">close</i>
        </button>
      </div>

      <div class="qr-modal-body">
        <!-- Controles de Estilo -->
        <div class="qr-style-selector mb-3">
          <button
            type="button"
            class="style-btn"
            :class="{ active: currentStyle === 'frame' }"
            @click="currentStyle = 'frame'"
          >
            <i class="material-icons text-sm me-1">aspect_ratio</i>
            Moldura Completa
          </button>
          <button
            type="button"
            class="style-btn"
            :class="{ active: currentStyle === 'clean' }"
            @click="currentStyle = 'clean'"
          >
            <i class="material-icons text-sm me-1">crop_free</i>
            QR Code Limpo
          </button>
        </div>

        <!-- Área Renderizável para Exportação (Captura html2canvas) -->
        <div class="qr-render-wrapper">
          <div
            ref="qrExportArea"
            class="qr-export-area"
            :class="{ 'qr-frame-style': currentStyle === 'frame', 'qr-clean-style': currentStyle === 'clean' }"
          >
            <div v-if="currentStyle === 'frame'" class="qr-frame-header">
              <span class="qr-frame-brand">RIFA365 🍀</span>
              <h6 class="qr-frame-title">{{ campaign?.name || 'Campanha Oficial' }}</h6>
              <p class="qr-frame-cta">Aponte a câmera do seu celular</p>
            </div>

            <div class="qr-code-box">
              <QrcodeVue
                :value="campaignUrl"
                :size="260"
                level="H"
                render-as="canvas"
              />
            </div>

            <div v-if="currentStyle === 'frame'" class="qr-frame-footer">
              <p class="qr-frame-link">{{ displayShortUrl }}</p>
              <span class="qr-frame-safe">Pagamento Seguro via Pix • Participação Imediata</span>
            </div>
          </div>
        </div>

        <!-- Link Direto e Informações -->
        <div class="qr-link-copy-box mt-3">
          <label class="text-xxs text-uppercase text-secondary font-weight-bolder mb-1 d-block">
            Link oficial da sua campanha:
          </label>
          <div class="d-flex align-items-center gap-2">
            <input
              type="text"
              readonly
              class="form-control form-control-sm qr-url-input"
              :value="campaignUrl"
              @focus="$event.target.select()"
            />
            <button
              type="button"
              class="btn btn-sm bg-gradient-dark mb-0 d-flex align-items-center px-3"
              @click="copyCampaignLink"
            >
              <i class="material-icons text-sm me-1">content_copy</i>
              Copiar
            </button>
          </div>
        </div>
      </div>

      <div class="qr-modal-footer">
        <button
          type="button"
          class="btn btn-outline-secondary mb-0 px-4"
          @click="close"
        >
          Fechar
        </button>

        <button
          type="button"
          class="btn bg-gradient-success mb-0 d-flex align-items-center shadow-success px-4"
          :disabled="downloading"
          @click="downloadQrCode"
        >
          <i class="material-icons me-2">{{ downloading ? 'hourglass_top' : 'download' }}</i>
          {{ downloadButtonLabel }}
        </button>
      </div>
    </div>

    <!-- Fallback mobile: preview para salvar com toque longo (iOS) -->
    <div
      v-if="showSavePreview"
      class="qr-save-preview-backdrop"
      @click.self="closeSavePreview"
    >
      <div class="qr-save-preview-card">
        <div class="qr-save-preview-header">
          <h6 class="mb-0 font-weight-bolder text-white">Salvar QR Code</h6>
          <button type="button" class="btn-close-modal" @click="closeSavePreview">
            <i class="material-icons">close</i>
          </button>
        </div>
        <p class="qr-save-preview-hint">
          <i class="material-icons text-sm align-middle me-1">touch_app</i>
          Segure o dedo na imagem abaixo e toque em <strong>Salvar imagem</strong>
        </p>
        <img
          v-if="previewImageUrl"
          :src="previewImageUrl"
          alt="QR Code da campanha"
          class="qr-save-preview-image"
        />
      </div>
    </div>
  </div>
</template>

<script>
import QrcodeVue from "qrcode.vue";
import html2canvas from "html2canvas";
import { buildCampaignPublicUrl } from "@/store/config.js";

export default {
  name: "CampaignQrCodeModal",
  components: {
    QrcodeVue,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    campaign: {
      type: Object,
      default: () => ({}),
    },
    user: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["close"],
  data() {
    return {
      currentStyle: "frame", // 'frame' ou 'clean'
      downloading: false,
      showSavePreview: false,
      previewImageUrl: "",
    };
  },
  computed: {
    campaignUrl() {
      if (!this.campaign?.slug) return "";
      return buildCampaignPublicUrl(this.campaign.slug, this.user);
    },
    displayShortUrl() {
      if (!this.campaignUrl) return "";
      return this.campaignUrl.replace(/^https?:\/\//, "");
    },
    isMobile() {
      if (typeof navigator === "undefined") return false;
      const ua = navigator.userAgent || "";
      const touchDevice =
        navigator.maxTouchPoints > 1 && window.matchMedia("(max-width: 1024px)").matches;
      return /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua) || touchDevice;
    },
    downloadButtonLabel() {
      if (this.downloading) return "Gerando Imagem...";
      return this.isMobile ? "Salvar / Compartilhar QR" : "Baixar Imagem (Alta Definição)";
    },
  },
  methods: {
    close() {
      this.closeSavePreview();
      this.$emit("close");
    },
    closeSavePreview() {
      this.showSavePreview = false;
      if (this.previewImageUrl) {
        URL.revokeObjectURL(this.previewImageUrl);
        this.previewImageUrl = "";
      }
    },
    buildFileName() {
      const safeName = (this.campaign?.name || "campanha")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "-");
      return `qrcode-${safeName}-${this.currentStyle}.png`;
    },
    canvasToBlob(canvas) {
      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error("Falha ao gerar imagem"))),
          "image/png",
          1
        );
      });
    },
    async shareImageFile(file) {
      if (typeof navigator.share !== "function" || typeof navigator.canShare !== "function") {
        return false;
      }
      if (!navigator.canShare({ files: [file] })) {
        return false;
      }
      await navigator.share({
        files: [file],
        title: "QR Code da Campanha",
        text: this.campaign?.name || "Campanha Rifa365",
      });
      return true;
    },
    triggerBlobDownload(blob, fileName) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.rel = "noopener";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 8000);
      return url;
    },
    openMobileSavePreview(blob) {
      this.closeSavePreview();
      this.previewImageUrl = URL.createObjectURL(blob);
      this.showSavePreview = true;
    },
    async copyCampaignLink() {
      try {
        await navigator.clipboard.writeText(this.campaignUrl);
        this.$toast.add({
          severity: "success",
          summary: "Link Copiado!",
          detail: "Link da campanha copiado para a área de transferência.",
          life: 2500,
        });
      } catch (err) {
        console.error("Erro ao copiar link:", err);
      }
    },
    async downloadQrCode() {
      if (!this.$refs.qrExportArea) return;
      this.downloading = true;

      try {
        await this.$nextTick();

        const canvas = await html2canvas(this.$refs.qrExportArea, {
          scale: 3,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
        });

        const fileName = this.buildFileName();
        const blob = await this.canvasToBlob(canvas);
        const file = new File([blob], fileName, { type: "image/png" });

        // Mobile: menu nativo (Salvar em Fotos / Arquivos / WhatsApp)
        if (this.isMobile) {
          try {
            const shared = await this.shareImageFile(file);
            if (shared) {
              this.$toast.add({
                severity: "success",
                summary: "QR Code pronto!",
                detail: "Escolha Salvar imagem ou Fotos no menu do celular.",
                life: 4000,
              });
              return;
            }
          } catch (shareErr) {
            if (shareErr?.name === "AbortError") return;
          }

          // Fallback iOS/Android: preview com toque longo para salvar
          this.openMobileSavePreview(blob);
          this.$toast.add({
            severity: "info",
            summary: "Como salvar no celular",
            detail: "Segure o dedo na imagem e toque em Salvar imagem.",
            life: 5000,
          });
          return;
        }

        // Desktop: download direto via Blob (mais confiável que data URL)
        this.triggerBlobDownload(blob, fileName);

        this.$toast.add({
          severity: "success",
          summary: "Download Concluído!",
          detail: "Imagem do QR Code baixada em alta qualidade.",
          life: 3000,
        });
      } catch (error) {
        console.error("Erro ao gerar QR Code para download", error);
        this.$toast.add({
          severity: "error",
          summary: "Erro no Download",
          detail: "Não foi possível gerar a imagem do QR Code.",
          life: 3000,
        });
      } finally {
        this.downloading = false;
      }
    },
  },
  beforeUnmount() {
    this.closeSavePreview();
  },
};
</script>

<style scoped lang="scss">
.qr-modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(15, 23, 42, 0.7);
  backdrop-filter: blur(4px);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.qr-modal-card {
  background: #ffffff;
  border-radius: 20px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: modalPop 0.22s ease-out;
}

@keyframes modalPop {
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.qr-modal-header {
  background: linear-gradient(135deg, #1e1b4b 0%, #312e81 100%);
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.qr-icon-bubble {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a5b4fc;
}

.btn-close-modal {
  background: transparent;
  border: none;
  color: #cbd5e1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  padding: 6px;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
}

.qr-modal-body {
  padding: 24px;
}

.qr-style-selector {
  display: flex;
  gap: 8px;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 12px;
}

.style-btn {
  flex: 1;
  border: none;
  background: transparent;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &.active {
    background: #ffffff;
    color: #1e1b4b;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

.qr-render-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  padding: 20px;
}

/* Estilo com Moldura para Impressão */
.qr-export-area.qr-frame-style {
  background: #ffffff;
  border-radius: 20px;
  padding: 24px 28px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: 2px solid #e2e8f0;
  text-align: center;
  max-width: 320px;
  width: 100%;
}

.qr-frame-header {
  margin-bottom: 16px;
}

.qr-frame-brand {
  font-size: 11px;
  font-weight: 900;
  color: #be185d;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  display: block;
  margin-bottom: 4px;
}

.qr-frame-title {
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 4px 0;
  line-height: 1.3;
}

.qr-frame-cta {
  font-size: 12px;
  color: #64748b;
  margin: 0;
  font-weight: 600;
}

.qr-code-box {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
}

/* Estilo Limpo */
.qr-export-area.qr-clean-style {
  background: #ffffff;
  padding: 16px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.qr-frame-footer {
  margin-top: 14px;
}

.qr-frame-link {
  font-size: 12px;
  font-weight: 700;
  color: #2563eb;
  margin: 0 0 4px 0;
  word-break: break-all;
}

.qr-frame-safe {
  font-size: 10px;
  color: #94a3b8;
  font-weight: 600;
  display: block;
}

.qr-link-copy-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
}

.qr-url-input {
  background: #ffffff !important;
  font-size: 12px;
  color: #334155;
  font-weight: 600;
}

.qr-modal-footer {
  padding: 16px 24px 20px;
  background: #ffffff;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.qr-save-preview-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1060;
  background: rgba(15, 23, 42, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.qr-save-preview-card {
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 360px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
}

.qr-save-preview-header {
  background: linear-gradient(135deg, #047857 0%, #059669 100%);
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.qr-save-preview-hint {
  margin: 0;
  padding: 12px 16px 8px;
  font-size: 13px;
  color: #475569;
  text-align: center;
  line-height: 1.4;
}

.qr-save-preview-image {
  display: block;
  width: 100%;
  height: auto;
  padding: 0 16px 16px;
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: default;
}
</style>
